import { ShieldCheck, Star } from 'lucide-react'
import { NEED_CATEGORY_LABELS, summarizeJson } from '../utils/taxonomy'
import type { ProviderDecisionSummary, ProviderOffer } from '../types/api'

/**
 * What a provider offers and how they are regarded, for whoever has to answer their request.
 *
 * <p>Shown on all three screens that carry a decision - a learner's inbox, a parent's approval
 * queue, and a parent's audit trail - so that the answer to "should I accept this?" is on the same
 * card as the buttons.
 *
 * <p>Nothing here is PII, which is why it is shown before anyone has approved anything. The name
 * above it is masked to initials until they have.
 */
export function ProviderDecisionCard({ provider }: { provider: ProviderDecisionSummary }) {
  const rated = provider.averageRating !== null && provider.ratingCount > 0

  return (
    <div className="mt-2 rounded-lg bg-ink-50 px-3 py-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {provider.verified && (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
            <ShieldCheck size={13} /> Verified
            {provider.verificationTier ? ` ${provider.verificationTier}` : ''}
          </span>
        )}
        {rated ? (
          <span className="inline-flex items-center gap-1 text-xs text-ink-600">
            <Star size={13} className="fill-sun-400 text-sun-400" />
            <strong className="font-semibold">{provider.averageRating!.toFixed(1)}</strong>
            <span className="text-ink-400">
              ({provider.ratingCount} {provider.ratingCount === 1 ? 'review' : 'reviews'})
            </span>
          </span>
        ) : (
          // Said rather than left blank: an absent rating and a bad one look identical if the row
          // simply disappears, and "no reviews yet" is a real thing to weigh.
          <span className="text-xs text-ink-400">No reviews yet</span>
        )}
      </div>

      {provider.services.length > 0 && (
        <ul className="mt-1.5 space-y-0.5">
          {provider.services.slice(0, 3).map((s, i) => (
            <li key={`${s.category}-${i}`} className="text-xs text-ink-600">
              <span className="font-semibold text-ink-800">
                {NEED_CATEGORY_LABELS[s.category] ?? s.category}
              </span>
              {itemsOf(s) ? <span className="text-ink-500"> · {itemsOf(s)}</span> : null}
            </li>
          ))}
          {provider.services.length > 3 && (
            <li className="text-xs text-ink-400">
              and {provider.services.length - 3} more
            </li>
          )}
        </ul>
      )}
    </div>
  )
}

function itemsOf(service: ProviderOffer): string {
  return summarizeJson(service.items)
}
